import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { useRestaurantSettings } from '../../../hooks/useRestaurantSettings';
import { formatTelHref } from '../../../helpers/phone';
import { serviceLines } from '../../../helpers/hours';
import { imageSources } from '../../../helpers/imageScales';
import InlineText from '../../InlineEdit/InlineText';
import InlineTextArea from '../../InlineEdit/InlineTextArea';
import './hero.scss';

const DEFAULTS = {
  eyebrow: 'Restaurant · Jodoigne · Cuisine française',
  heading: 'Sous la lumière du jour.',
};

const View = (props: any) => {
  const { data, isEditMode, imageSlot, block, onChangeBlock } = props;
  const settings = useRestaurantSettings();

  const update = (field: string, value: string) =>
    onChangeBlock?.(block, { ...data, [field]: value });

  const variant = data.variant || 'editorial';
  const sources =
    data.image && Array.isArray(data.image) && data.image.length > 0
      ? imageSources(
          data.image[0],
          'great',
          variant === 'split' ? '(min-width: 720px) 50vw, 100vw' : '100vw',
        )
      : null;

  const eyebrow = data.eyebrow ?? (isEditMode ? '' : DEFAULTS.eyebrow);
  const heading = data.heading ?? (isEditMode ? '' : DEFAULTS.heading);
  const description = data.description ?? '';

  const showAddress = data.show_address ?? true;
  const showService = data.show_service ?? true;
  const showPhone = data.show_phone ?? true;
  const addressOverride = data.address_override ?? '';
  // The service lines come from the Hours block, through the settings.
  const serviceText = serviceLines(settings.opening_hours).join('\n');
  const phoneOverride = data.phone_override ?? '';

  const phoneNumber = phoneOverride || settings.phone_primary || '';
  const phoneHref = formatTelHref(phoneNumber);

  const defaultImageElement = sources ? (
    <img
      className="hero-img"
      src={sources.src}
      srcSet={sources.srcSet}
      sizes={sources.sizes}
      alt={data.image_alt || ''}
      loading="eager"
      fetchPriority="high"
    />
  ) : null;

  const slotted = imageSlot?.({
    fieldId: 'image',
    value: data.image,
    element: defaultImageElement,
  });

  const heroImage =
    slotted ??
    defaultImageElement ??
    (isEditMode && (
      <div className="hero-img hero-img--placeholder">
        Sélectionner une image hero
      </div>
    ));

  const renderEyebrow = () => {
    if (isEditMode) {
      return (
        <InlineText
          value={eyebrow}
          onChange={(v: string) => update('eyebrow', v)}
          renderTag="p"
          renderClassName="hero-eyebrow"
          placeholder="Surtitre"
        />
      );
    }
    return eyebrow ? <p className="hero-eyebrow">{eyebrow}</p> : null;
  };

  const renderHeading = () => {
    if (isEditMode) {
      return (
        <InlineText
          value={heading}
          onChange={(v: string) => update('heading', v)}
          renderTag="h1"
          renderClassName="display"
          placeholder="Titre principal"
        />
      );
    }
    return heading ? <h1 className="display">{heading}</h1> : null;
  };

  const renderDescription = () => {
    if (!description && !isEditMode) return null;
    if (isEditMode) {
      return (
        <InlineTextArea
          value={description}
          onChange={(v: string) => update('description', v)}
          renderTag="div"
          renderClassName="hero-description"
          placeholder="Texte de description (optionnel)"
        />
      );
    }
    return (
      <div className="hero-description">
        {description.split('\n').map((paragraph: string, i: number) => (
          <p key={i}>{paragraph}</p>
        ))}
      </div>
    );
  };

  const renderMetaLines = (content: string) =>
    content.split('\n').map((line: string, i: number, arr: string[]) => (
      <span key={i}>
        {line}
        {i < arr.length - 1 && <br />}
      </span>
    ));

  const metaBlock = (
    <div className="hero-meta">
      {showAddress && (
        <div>
          <strong>Adresse</strong>
          {addressOverride
            ? renderMetaLines(addressOverride)
            : renderMetaLines(
                `${settings.address_line1 ?? ''}\n${settings.postal_code ?? ''} ${settings.city ?? ''}`.trim(),
              )}
        </div>
      )}
      {showService && serviceText && (
        <div>
          <strong>Service</strong>
          {renderMetaLines(serviceText)}
        </div>
      )}
      {showPhone && phoneNumber && (
        <div>
          <strong>Réservation</strong>
          <a href={phoneHref} className="h-phone">
            {phoneNumber}
          </a>
          <span className="h-online">
            ou <UniversalLink href="/#reservation">en ligne</UniversalLink>
          </span>
        </div>
      )}
    </div>
  );

  if (variant === 'split') {
    const imageSide = data.image_side || 'right';
    const splitClass = `hero hero--split${imageSide === 'left' ? ' hero--split-reversed' : ''}`;
    return (
      <section className={splitClass}>
        <div className="shell hero-split-grid">
          <div className="hero-split-text">
            {renderEyebrow()}
            {renderHeading()}
            {renderDescription()}
            {metaBlock}
          </div>
          <div className="hero-split-media">{heroImage}</div>
        </div>
      </section>
    );
  }

  return (
    <section className="hero hero--editorial">
      {heroImage}
      <div className="hero-overlay" />
      <div className="hero-content shell">
        <div className="hero-caption">
          {renderEyebrow()}
          {renderHeading()}
          {renderDescription()}
          {metaBlock}
        </div>
      </div>
    </section>
  );
};

export default View;
